/**
 * "Dashboard filter" mode of the AVA Inspect list (AVA-560 KPI drill to list).
 *
 * A dashboard number that drills to its list hands the AVA Inspect side panel the exact ids it
 * counted. While the filter is active the list shows only those ids, a banner names the mode, and
 * Clear returns the normal list. The filter remembers the facility it was made on, so switching
 * facility drops it instead of showing another facility's ids.
 *
 * The ids are read page by page through `getTagsByIds` rather than through the list's `/Search`
 * call, because that endpoint's query (TagSearchQuery) has no id filter.
 *
 * PURE: no Vue, no store, no I/O, no aliased imports (unit-tested under `node --test`).
 */

/** Page size of the list in this mode; the normal list loads 50 at a time too. */
export const DASHBOARD_FILTER_PAGE_SIZE = 50;

/** HTTP status getTagsByIds answers when none of a page's ids resolves in this facility. */
export const NO_TAGS_FOUND_STATUS = 400;

let nextRequestId = 0;

// Ids compare case-insensitively, as SQL Server does: the values endpoint and getTagsByIds may
// spell the same GUID in different cases.
const idKey = id => String(id).toLowerCase();

/** Trimmed, non-blank ids without case-insensitive duplicates; the first spelling is kept. */
function uniqueIds(ids) {
  const seen = new Set();
  const result = [];
  (Array.isArray(ids) ? ids : []).forEach((value) => {
    const id = value == null ? '' : String(value).trim();
    if (!id || seen.has(idKey(id))) return;
    seen.add(idKey(id));
    result.push(id);
  });
  return result;
}

/**
 * A new, frozen filter. Ids are trimmed, blanks dropped and duplicates removed ignoring case
 * (order kept).
 * Every call gets a fresh requestId so a second drill to the same ids still re-queries.
 * @param {{ ids: Array<*>, total?: number, facilityId: string }} input
 * @returns {{ ids: string[], total: number, facilityId: string, requestId: number }|null}
 *   null when there is no id or no facility
 */
export function createDashboardIdFilter({ ids, total, facilityId } = {}) {
  const unique = uniqueIds(ids);
  if (!unique.length || !facilityId) return null;
  nextRequestId += 1;
  return Object.freeze({
    ids: Object.freeze(unique),
    total: Number.isFinite(total) && total > unique.length ? total : unique.length,
    facilityId: String(facilityId),
    requestId: nextRequestId,
  });
}

/**
 * The filter when it applies to the current facility, otherwise null.
 * @param {object|null} filter
 * @param {string|null} facilityId - the current facility (store.state.assetId)
 * @returns {object|null}
 */
export function activeDashboardIdFilter(filter, facilityId) {
  if (!filter || !Array.isArray(filter.ids) || !filter.ids.length) return null;
  if (!facilityId || String(facilityId) !== filter.facilityId) return null;
  return filter;
}

/**
 * Watch key for the panel: changes on every new drill, '' when inactive.
 * @param {object|null} activeFilter - from activeDashboardIdFilter
 * @returns {string}
 */
export function dashboardIdFilterKey(activeFilter) {
  return activeFilter ? `${activeFilter.requestId}:${activeFilter.ids.length}` : '';
}

/**
 * The ids the list may show: the dashboard ids, narrowed to the register-scoped ids when that
 * filter is active too (both are filters, so they AND).
 * @param {string[]} dashboardIds
 * @param {string[]|null} registerIds - null when no register filter is active
 * @returns {string[]}
 */
export function scopedDashboardIds(dashboardIds, registerIds) {
  const ids = Array.isArray(dashboardIds) ? dashboardIds : [];
  if (!Array.isArray(registerIds)) return [...ids];
  const allowed = new Set(registerIds.map(idKey));
  return ids.filter(id => allowed.has(idKey(id)));
}

/**
 * One page of ids to fetch.
 * @param {string[]} ids
 * @param {number} skip - how many are already listed
 * @param {number} [take]
 * @returns {string[]}
 */
export function dashboardIdPage(ids, skip, take = DASHBOARD_FILTER_PAGE_SIZE) {
  const list = Array.isArray(ids) ? ids : [];
  const start = Math.max(0, Number(skip) || 0);
  return list.slice(start, start + take);
}

/**
 * Orders fetched tags by the page's id order (getTagsByIds returns them unordered) and drops any
 * tag that was not asked for.
 * @param {Array<{ id: string }>} tags
 * @param {string[]} pageIds
 * @returns {Array}
 */
export function orderTagsByIds(tags, pageIds) {
  const byId = new Map((Array.isArray(tags) ? tags : [])
    .filter(tag => tag && tag.id != null)
    .map(tag => [idKey(tag.id), tag]));
  return (pageIds || []).map(id => byId.get(idKey(id))).filter(Boolean);
}

/**
 * The list state after one page. `offset` counts ids CONSUMED (not tags shown), because an id can
 * resolve to nothing (deleted since the dashboard read it, or outside this facility); paging by the
 * number of listed tags would then skip or repeat ids. `count` is the best known total: every id
 * not yet fetched, plus the tags actually listed, so it becomes exact once the last page lands and
 * "Load more" (shown while listed != count) disappears exactly when every id was consumed.
 * @param {{ ids: string[], reload: boolean, offset: number, listed: Array, pageIds: string[],
 *           fetched: Array }} input
 * @returns {{ tags: Array, offset: number, count: number }}
 */
export function nextDashboardPage({ ids, reload, offset, listed, pageIds, fetched }) {
  const all = Array.isArray(ids) ? ids : [];
  const pageTags = orderTagsByIds(fetched, pageIds);
  const tags = reload ? pageTags : [...(Array.isArray(listed) ? listed : []), ...pageTags];
  const start = reload ? 0 : Math.max(0, Number(offset) || 0);
  const consumed = Math.min(all.length, start + (Array.isArray(pageIds) ? pageIds.length : 0));
  return { tags, offset: consumed, count: all.length - consumed + tags.length };
}

/**
 * The N of the banner: the list's own total once a page of this mode has loaded (it is then exact
 * about what the list can show), the drilled id count before that - never the previous, unfiltered
 * list's total.
 * @param {object|null} activeFilter
 * @param {number} offset - ids consumed so far in this mode (0 until the first page lands)
 * @param {number} listCount - the list's total
 * @returns {number}
 */
export function dashboardFilterCount(activeFilter, offset, listCount) {
  if (!activeFilter) return 0;
  return offset > 0 ? Number(listCount) || 0 : activeFilter.ids.length;
}

/**
 * Banner copy, e.g. "Showing 12 AVA Inspects from the dashboard".
 * @param {number} count
 * @returns {string}
 */
export function dashboardFilterBannerText(count) {
  const n = Number(count) || 0;
  return `Showing ${n} AVA ${n === 1 ? 'Inspect' : 'Inspects'} from the dashboard`;
}
